import { useQuery } from '@tanstack/react-query'
import { Globe } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { fetchCustomDomains, type CustomDomain } from '../../lib/customDomains'
import { cn } from '../../utils/cn'

/**
 * Owner-only top-bar chip showing the tenant's custom-domain status (Phase 0.3).
 * amber = pending/verifying, green = live. Renders nothing when no domain is
 * connected. Reads get_custom_domains (member RPC) and reduces to the primary
 * (or first) domain.
 */
export function CustomDomainChip() {
  const { tenant, role } = useAuthStore()
  const tid = tenant?.id ?? ''

  const { data: domains = [] } = useQuery<CustomDomain[]>({
    queryKey: ['custom-domains-chip', tid],
    enabled: !!tid && role === 'owner',
    refetchInterval: 5 * 60 * 1000,
    queryFn: () => fetchCustomDomains(tid),
  })

  if (!tenant || role !== 'owner') return null

  const active = domains.filter((d) => d.status !== 'removed')
  if (active.length === 0) return null
  const d = active.find((x) => x.is_primary) ?? active[0]

  const isLive = d.status === 'active'
  const isPending = d.status === 'pending_dns' || d.status === 'verifying'
  const isFailed = d.status === 'failed'

  return (
    <Link
      to="/business-settings/commerce"
      title={d.domain}
      className={cn(
        'hidden md:flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors max-w-[180px]',
        isLive
          ? 'bg-sage-100 text-sage-700 hover:bg-sage-200'
          : isFailed
          ? 'bg-red-100 text-red-700 hover:bg-red-200'
          : 'bg-amber-100 text-amber-700 hover:bg-amber-200',
      )}
    >
      <Globe className="h-3 w-3 flex-none" />
      <span className="truncate">
        {isLive ? d.domain : isPending ? 'Domain: verifying' : isFailed ? 'Domain: failed' : d.domain}
      </span>
    </Link>
  )
}
